'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LYRRA_A11Y_UPDATED_EVENT } from '@/hooks/useKeyboardNavigationSetting';
import {
  applyGlobalSimplifiedTextIncremental,
  applyGlobalSimplifiedTextToPage,
  isGlobalSimplifiedTextApplied,
  isSimplifiedTextManagedByEduflowPlayer,
  readSimplifiedTextModeEnabled,
} from '@/lib/global-simplified-text';

/**
 * Applique le mode texte simplifié sur toute page (hors lecteur EduFlow).
 * Réagit au changement de route et à l'événement accessibilité.
 */
export function GlobalSimplifiedText() {
  const location = useLocation();
  const { pathname, search } = location;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const runRef = useRef(0);

  useEffect(() => {
    const gen = ++runRef.current;
    const enabled = readSimplifiedTextModeEnabled();

    if (!enabled || isSimplifiedTextManagedByEduflowPlayer()) {
      void applyGlobalSimplifiedTextToPage(false);
      return;
    }

    setLoading(true);
    void applyGlobalSimplifiedTextToPage(true)
      .catch(() => {
        /* erreurs gérées par A11yToolbar lors du toggle manuel */
      })
      .finally(() => {
        if (runRef.current === gen) setLoading(false);
      });

    return () => {
      if (runRef.current === gen) {
        void applyGlobalSimplifiedTextToPage(false);
      }
    };
  }, [pathname, search]);

  useEffect(() => {
    if (!readSimplifiedTextModeEnabled() || isSimplifiedTextManagedByEduflowPlayer()) return;
    const root = document.getElementById('main-content') ?? document.body;
    let timer: number | undefined;
    const obs = new MutationObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void applyGlobalSimplifiedTextIncremental().catch(() => {
          /* silencieux : le contenu peut être en cours de chargement */
        });
      }, 700);
    });
    obs.observe(root, { childList: true, subtree: true });
    return () => {
      obs.disconnect();
      window.clearTimeout(timer);
    };
  }, [pathname, search]);

  useEffect(() => {
    const onA11y = () => {
      const enabled = readSimplifiedTextModeEnabled();
      if (isSimplifiedTextManagedByEduflowPlayer()) return;
      if (!enabled) {
        void applyGlobalSimplifiedTextToPage(false);
        return;
      }
      if (isGlobalSimplifiedTextApplied()) return;
      const gen = ++runRef.current;
      setLoading(true);
      void applyGlobalSimplifiedTextToPage(true).finally(() => {
        if (runRef.current === gen) setLoading(false);
      });
    };
    window.addEventListener(LYRRA_A11Y_UPDATED_EVENT, onA11y);
    return () => window.removeEventListener(LYRRA_A11Y_UPDATED_EVENT, onA11y);
  }, []);

  if (!loading || typeof document === 'undefined') return null;

  return createPortal(<SimplifiedTextLoadingOverlay t={t} />, document.body);
}

function SimplifiedTextLoadingOverlay({ t }: { t: (key: string, def: string) => string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="fixed inset-0 z-[10048] flex items-center justify-center bg-black/25 backdrop-blur-[2px]"
    >
      <div className="mx-4 max-w-md rounded-2xl border border-gray-200 bg-white px-6 py-5 text-center shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
        <p className="text-sm font-bold uppercase tracking-wider text-[#0C1C38] dark:text-zinc-100">
          {t('accessibility.simplifiedTextMode', 'Mode texte simplifié')}
        </p>
        <p className="mt-2 text-sm text-gray-600 dark:text-zinc-400">
          {t(
            'accessibility.simplifiedTextPageLoading',
            'Simplification du contenu de cette page par IA…',
          )}
        </p>
      </div>
      </div>
  );
}
